import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Import Shared Components
import { Loader, MFASetupForm } from 'shared-components';
import {
  MFAStorage,
  MFAErrors,
  completeMFASetup as genericCompleteMFASetup,
} from 'shared-components/src/utils/mfa';
import { getSubdomain } from 'shared-components/src/utils/whitelabel';
// Import Local Components
import AuthForm from './AuthForm';


function MFASetup() {
  const navigate = useNavigate();
  const [mfaData, setMfaData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Generate dynamic branding info
  const getBrandingInfo = () => {
    const { hostname } = window.location;
    const subdomain = getSubdomain(hostname);

    // Use full subdomain for branding, replace dots with spaces
    // For hrblockadvantagecard.staging.payhubportal.io -> "hrblockadvantagecard staging Payhub"
    let companyName;
    if (subdomain && subdomain.toUpperCase() !== 'UNBRANDED') {
      const formattedSubdomain = subdomain.replace(/\./g, ' ');
      companyName = `${formattedSubdomain} Payhub`;
    } else {
      companyName = 'Berkeley Payhub';
    }

    return {
      issuer: companyName,
      username: mfaData.user_name || 'Client User', // Use username from backend (email for client portal)
      domain: hostname,
    };
  };

  const getErrorMessage = errorCode => {
    // Map error codes to user-friendly messages (Client Portal - English only)
    const errorMessages = {
      'errors-invalid-totp-code':
        'Invalid verification code. Please try again.',
      'errors-mfa-not-setup':
        'MFA is not set up for your account. Please contact support.',
      'errors-mfa-session-error':
        'Your session has expired. Please log in again.',
      'errors-user-not-found': 'User not found. Please log in again.',
      'errors-too-many-attempts':
        'Too many failed attempts. Please try again later.',
      'errors-rate-limited':
        'Too many requests. Please wait a moment and try again.',
      'errors-unauthorized': 'Authentication failed. Please log in again.',
      'errors-mfa-setup-failed': 'MFA setup failed. Please try again.',
      'errors-internal-error': 'An error occurred. Please try again later.',
      'errors-unexpected': 'An unexpected error occurred. Please try again.',
    };

    return (
      errorMessages[errorCode] ||
      'An unexpected error occurred. Please try again.'
    );
  };

  useEffect(() => {
    // Retrieve MFA setup data from sessionStorage
    const setupData = MFAStorage.getSetupData();

    if (!setupData) {
      // No setup data found, redirect to login
      navigate('/auth/login');
      return;
    }

    setMfaData(setupData);
  }, [navigate]);

  const handleMFASetupComplete = async (userSub, session, totpCode) => {
    try {
      setSubmitting(true);
      setFormError(null);

      // Use the generic MFA setup utility with client-specific endpoint
      await genericCompleteMFASetup(
        '/api/v1/client_portal/mfa/setup',
        userSub,
        session,
        totpCode,
        // onSuccess callback
        () => {
          // Clear MFA session data
          MFAStorage.clearAll();
          // Redirect to dashboard
          window.location.pathname = '/';
        },
        // onError callback
        error => {
          console.error('MFA setup failed:', error);
          setSubmitting(false);

          // Check if it's an authentication error or validation error
          if (MFAErrors.isAuthError(error)) {
            // Redirect to login for authentication errors
            navigate('/auth/login', {
              state: {
                error: error.message,
                errorDetails: error.details,
              },
            });
          } else {
            // Keep on page for validation errors (wrong TOTP code, etc.)
            const errorCode = MFAErrors.getUserFriendlyError(error);
            const errorMessage = getErrorMessage(errorCode);
            setFormError(errorMessage);
          }
        }
      );
    } catch (error) {
      // Error already handled in onError callback
      console.error('Unexpected error in MFA setup:', error);
    }
  };

  const handleSkipMFASetup = () => {
    // Clear MFA session data
    MFAStorage.clearAll();
    // Redirect to dashboard, allowing user to skip MFA setup for now
    window.location.pathname = '/';
  };

  if (!mfaData) {
    return (
      <AuthForm
        title="Two-Factor Authentication Setup"
        submitButtonText="Continue"
        onSubmit={e => e.preventDefault()}
      >
        <Loader />
      </AuthForm>
    );
  }

  return (
    <AuthForm
      title="Two-Factor Authentication Setup"
      error={formError}
      onSubmit={e => {
        e.preventDefault();
        // Form submission is handled by MFASetupForm onComplete
      }}
      submitButtonText="Continue"
      secondaryButtonText="Skip for Now"
      onClick={handleSkipMFASetup}
    >
      {submitting ? (
        <Loader />
      ) : (
        (() => {
          const brandingInfo = getBrandingInfo();
          return (
            <MFASetupForm
              mfaData={mfaData}
              onSetupComplete={handleMFASetupComplete}
              loading={submitting}
              error={formError}
              issuer={brandingInfo.issuer}
              username={brandingInfo.username}
              disabled={submitting}
              autoFocus
              labels={{
                qrInstruction: `Scan this QR code with your authenticator app for ${brandingInfo.issuer}:`,
                manualEntryLabel: 'Manual Entry',
                authenticatorApps:
                  'Compatible with Google Authenticator, Authy, Microsoft Authenticator, and other TOTP apps',
                otpLabel: 'Verification Code',
              }}
            />
          );
        })()
      )}
    </AuthForm>
  );
}

export default MFASetup;
